import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { profileService, UserProfile } from '../services/profile.service';
import { EditProfileModal } from '../components/profile/EditProfileModal';
import { DeleteAccountModal } from '../components/profile/DeleteAccountModal';
import { VotingHistoryTab } from '../components/profile/VotingHistoryTab';
import { PostsTab } from '../components/profile/PostsTab';
import { CommentsTab } from '../components/profile/CommentsTab';
import { GroupsTab } from '../components/profile/GroupsTab';

type TabType = 'votaciones' | 'posts' | 'comentarios' | 'grupos';

export function ProfilePage() {
  const { userId } = useParams<{ userId?: string }>();
  const { user, profile: currentUserProfile } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [stats, setStats] = useState({
    totalVotes: 0,
    totalPosts: 0,
    totalComments: 0,
    memberSince: null as string | null,
  });
  const [activeTab, setActiveTab] = useState<TabType>('votaciones');
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Determine which profile to show
  const profileUserId = userId || user?.id;
  const isOwnProfile = user?.id === profileUserId;

  useEffect(() => {
    if (!profileUserId) {
      navigate('/login');
      return;
    }

    loadProfile();
  }, [profileUserId]);

  const loadProfile = async () => {
    if (!profileUserId) return;

    setLoading(true);
    try {
      const [profileData, statsData] = await Promise.all([
        profileService.getProfile(profileUserId),
        profileService.getProfileStats(profileUserId),
      ]);

      setProfile(profileData);
      setStats(statsData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdated = () => {
    loadProfile();
    setShowEditModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 md:pb-0">
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 md:pb-0">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Perfil no encontrado
            </h1>
            <button
              onClick={() => navigate('/')}
              className="text-violet-600 hover:text-violet-700 dark:text-violet-400"
            >
              Volver al inicio
            </button>
          </div>
        </div>
      </div>
    );
  }

  const displayName = profile.display_name || profile.username || 'Usuario';
  const memberSinceDate = stats.memberSince
    ? new Date(stats.memberSince).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
      })
    : null;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 md:pb-0">
      {/* Banner */}
      <div className="relative">
        <div
          className="h-32 md:h-48 bg-gradient-to-r from-violet-500 to-fuchsia-600"
          style={
            profile.banner_url
              ? {
                  backgroundImage: `url(${profile.banner_url})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : undefined
          }
        />

        {/* Avatar */}
        <div className="absolute bottom-0 left-4 transform translate-y-1/2">
          <div className="relative">
            {profile.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={displayName}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white dark:border-gray-900 object-cover bg-white"
              />
            ) : (
              <div className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-white dark:border-gray-900 bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center">
                <span className="text-3xl md:text-4xl font-bold text-white">
                  {displayName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Profile Info */}
      <div className="container mx-auto px-4 mt-16 md:mt-20">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {displayName}
            </h1>
            {profile.username && (
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                @{profile.username}
              </p>
            )}
            {profile.bio && (
              <p className="mt-3 text-gray-700 dark:text-gray-300 max-w-2xl">
                {profile.bio}
              </p>
            )}
            {memberSinceDate && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Miembro desde {memberSinceDate}
              </p>
            )}
          </div>

          {isOwnProfile && (
            <div className="flex gap-2 mt-4 md:mt-0">
              <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium"
              >
                Editar Perfil
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              >
                🗑️
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.totalVotes}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Votaciones</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.totalPosts}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Posts</div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.totalComments}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Comentarios</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('votaciones')}
              className={`px-4 py-3 font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'votaciones'
                  ? 'border-violet-600 text-violet-600 dark:text-violet-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Votaciones
            </button>
            <button
              onClick={() => setActiveTab('posts')}
              className={`px-4 py-3 font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'posts'
                  ? 'border-violet-600 text-violet-600 dark:text-violet-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Posts
            </button>
            <button
              onClick={() => setActiveTab('comentarios')}
              className={`px-4 py-3 font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'comentarios'
                  ? 'border-violet-600 text-violet-600 dark:text-violet-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Comentarios
            </button>
            <button
              onClick={() => setActiveTab('grupos')}
              className={`px-4 py-3 font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === 'grupos'
                  ? 'border-violet-600 text-violet-600 dark:text-violet-400'
                  : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              Grupos
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="pb-8">
          {activeTab === 'votaciones' && profileUserId && (
            <VotingHistoryTab userId={profileUserId} isOwnProfile={isOwnProfile} />
          )}
          {activeTab === 'posts' && profileUserId && (
            <PostsTab userId={profileUserId} isOwnProfile={isOwnProfile} />
          )}
          {activeTab === 'comentarios' && profileUserId && (
            <CommentsTab userId={profileUserId} isOwnProfile={isOwnProfile} />
          )}
          {activeTab === 'grupos' && profileUserId && (
            <GroupsTab userId={profileUserId} isOwnProfile={isOwnProfile} />
          )}
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditProfileModal
          profile={profile}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleProfileUpdated}
        />
      )}

      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
}
